import { sanitizeHtml } from "@/utils/sanitizeHtml";

export function parseContentToAst(content) {
  if (!content) {
    return { version: 1, blocks: [] };
  }

  const trimmed = content.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && parsed.version && Array.isArray(parsed.blocks)) {
        return parsed;
      }
    } catch {
      // fall through
    }
  }

  const hasHtml = /<[a-z][\s\S]*>/i.test(content);
  if (hasHtml) {
    const blocks = parseHtmlToBlocks(content);
    if (blocks.length > 0) {
      return { version: 1, blocks };
    }
    return {
      version: 1,
      blocks: [{ type: "raw_html", html: content }],
    };
  }

  return {
    version: 1,
    blocks: parsePlainTextToBlocks(content),
  };
}

export function renderAstToHtml(ast) {
  if (!ast || !Array.isArray(ast.blocks)) return "";
  const html = ast.blocks.map(renderBlockToHtml).join("\n");
  return sanitizeHtml(html);
}

function renderBlockToHtml(block) {
  if (!block || !block.type) return "";
  switch (block.type) {
    case "heading": {
      const level = clampHeadingLevel(block.level);
      const text = escapeHtml(block.text || "");
      return `<h${level}>${convertInlineFormatting(text)}</h${level}>`;
    }
    case "paragraph": {
      const text = escapeHtml(block.text || "");
      return `<p>${convertInlineFormatting(text)}</p>`;
    }
    case "blockquote": {
      return renderBlockquoteHtml(block);
    }
    case "page_break": {
      return '<div class="page-break"></div>';
    }
    case "references": {
      return renderReferencesHtml(block);
    }
    case "table": {
      return renderTableHtml(block);
    }
    case "figure": {
      return renderFigureHtml(block);
    }
    case "raw_html": {
      return block.html || "";
    }
    default:
      return "";
  }
}

function renderTableHtml(block) {
  const caption = block.caption ? escapeHtml(block.caption) : "";
  const rows = parseTableRows(block.rowsText || "");
  if (rows.length === 0) return "";

  const width = Number.isFinite(block.width) ? block.width : 100;
  const layout = block.layout === "auto" ? "auto" : "fixed";
  const paddingY = Number.isFinite(block.cellPaddingY) ? block.cellPaddingY : 2;
  const paddingX = Number.isFinite(block.cellPaddingX) ? block.cellPaddingX : 4;
  const lineHeight = Number.isFinite(block.lineHeight) ? block.lineHeight : 9.5;
  const tableStyle = `width: ${width}%; table-layout: ${layout};`;
  const cellStyle = `padding: ${paddingY}px ${paddingX}px; line-height: ${lineHeight}pt;`;
  const colgroup = buildColgroup(block.columnWidths, rows);

  const thead = rows[0];
  const tbody = rows.slice(1);
  const maxCols = Math.max(...rows.map((r) => r.length));

  let html = `<div class="table-container"><table class="table-basic" style="${tableStyle}">`;
  if (colgroup) {
    html += colgroup;
  }
  if (caption) {
    html += `<caption>${caption}</caption>`;
  }
  html += "<thead><tr>";
  for (let c = 0; c < maxCols; c++) {
    const cell = escapeHtml(thead[c] || "");
    html += `<th style="${cellStyle}">${convertInlineFormatting(cell)}</th>`;
  }
  html += "</tr></thead>";

  if (tbody.length > 0) {
    html += "<tbody>";
    tbody.forEach((row) => {
      html += "<tr>";
      for (let c = 0; c < maxCols; c++) {
        const cell = escapeHtml(row[c] || "");
        html += `<td style="${cellStyle}">${convertInlineFormatting(cell)}</td>`;
      }
      html += "</tr>";
    });
    html += "</tbody>";
  }

  html += "</table></div>";
  return html;
}

function renderFigureHtml(block) {
  const rawCaption = block.caption || "";
  const caption = rawCaption ? escapeHtml(rawCaption) : "";
  const isTableCaption = rawCaption.trim().startsWith("<표");
  const src = block.src || "";
  const width = Number.isFinite(block.width) && block.width > 0 ? block.width : null;
  const height = Number.isFinite(block.height) && block.height > 0 ? block.height : null;
  const sizeStyle = [width ? `width: ${width}px` : "", height ? `height: ${height}px` : ""]
    .filter(Boolean)
    .join("; ");
  const shouldCaptionBeBottom = !isTableCaption;
  let html = `<div class="figure-container${shouldCaptionBeBottom ? " figure-caption-bottom" : ""}">`;
  if (src) {
    html += `<img src="${escapeHtml(src)}" alt="figure"${sizeStyle ? ` style="${sizeStyle}"` : ""} />`;
  } else {
    html += '<div class="figure-placeholder">[그림 영역]</div>';
  }
  if (caption) {
    html += `<div class="figure-caption">${caption}</div>`;
  }
  html += "</div>";
  return html;
}

function parsePlainTextToBlocks(text) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let paragraphBuf = [];
  let blockquoteBuf = [];

  const flushParagraph = () => {
    if (paragraphBuf.length === 0) return;
    blocks.push({ type: "paragraph", text: paragraphBuf.join(" ").trim() });
    paragraphBuf = [];
  };

  const flushBlockquote = () => {
    if (blockquoteBuf.length === 0) return;
    blocks.push({ type: "blockquote", text: blockquoteBuf.join("\n"), variant: 1, title: "" });
    blockquoteBuf = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "---") {
      flushParagraph();
      flushBlockquote();
      blocks.push({ type: "page_break" });
      continue;
    }

    if (trimmed.startsWith(">")) {
      flushParagraph();
      blockquoteBuf.push(trimmed.replace(/^>\s*/, ""));
      continue;
    }

    if (blockquoteBuf.length > 0) {
      flushBlockquote();
    }

    const heading = parseHeading(trimmed);
    if (heading) {
      flushParagraph();
      blocks.push({
        type: "heading",
        level: heading.level,
        text: heading.text,
      });
      continue;
    }

    if (trimmed === "") {
      flushParagraph();
      continue;
    }

    paragraphBuf.push(trimmed);
  }

  flushParagraph();
  flushBlockquote();
  return blocks;
}

function parseHeading(line) {
  if (!line) return null;
  const md = line.match(/^(#{1,3})\s+(.+)$/);
  if (md) {
    return { level: md[1].length + 1, text: md[2].trim() };
  }
  const numbered = line.match(/^(\d+(\.\d+)*)\s+(.+)$/);
  if (numbered) {
    const depth = numbered[1].split(".").length;
    const level = Math.min(4, depth + 1);
    return { level, text: `${numbered[1]} ${numbered[3]}`.trim() };
  }
  return null;
}

function parseTableRows(rowsText) {
  return rowsText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) =>
      line
        .split("|")
        .map((cell) => cell.trim().replace(/\\n/g, "\n"))
        .filter((cell) => cell !== "")
    );
}

function clampHeadingLevel(level) {
  const num = Number(level) || 2;
  return Math.min(6, Math.max(2, num));
}

function convertInlineFormatting(text) {
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/___(.+?)___/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/_([^_]+)_/g, "<em>$1</em>")
    .replace(/\^(\d+)\)/g, '<sup data-footnote-id="$1">$1)</sup>')
    .replace(/\n/g, "<br>");
}

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function parseHtmlToBlocks(html) {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return [];
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const container = doc.body.firstChild;
  if (!container) return [];

  const blocks = [];
  const children = Array.from(container.childNodes);

  children.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) blocks.push({ type: "paragraph", text });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;
    const tag = el.tagName.toLowerCase();

    if (tag === "div" && el.classList.contains("page-break")) {
      blocks.push({ type: "page_break" });
      return;
    }

    if (tag === "div" && el.classList.contains("references-title")) {
      const items = [];
      let next = el.nextSibling;
      while (next) {
        if (next.nodeType === Node.ELEMENT_NODE) {
          const nextEl = next;
          if (nextEl.classList.contains("reference-item")) {
            items.push(inlineToText(nextEl).trim());
            next = nextEl.nextSibling;
            continue;
          }
          if (nextEl.classList.contains("page-break")) break;
        }
        break;
      }
      blocks.push({ type: "references", itemsText: items.join("\n") });
      return;
    }

    if (tag === "blockquote" || (tag === "div" && el.classList.contains("blockquote"))) {
      const text = inlineToText(el).trim();
      if (text) blocks.push({ type: "blockquote", text, variant: 1, title: "" });
      return;
    }

    if (tag === "div" && el.classList.contains("box-style1")) {
      const items = Array.from(el.querySelectorAll(".box-style1-list-item p"))
        .map((p) => p.textContent?.trim())
        .filter(Boolean);
      const title = el.querySelector(".box-style1-title-text")?.textContent?.trim() || "";
      blocks.push({ type: "blockquote", text: items.join("\n"), variant: 1, title });
      return;
    }

    if (tag === "div" && el.classList.contains("box-style2")) {
      const text = el.querySelector(".box-style2-card-text")?.textContent?.trim() || inlineToText(el).trim();
      const title = el.querySelector(".box-style2-card-title")?.textContent?.trim() || "";
      if (text) blocks.push({ type: "blockquote", text, variant: 2, title });
      return;
    }

    if (tag === "div" && el.classList.contains("box-style3")) {
      const items = Array.from(el.querySelectorAll(".box-style3-list li"))
        .map((li) => li.textContent?.trim())
        .filter(Boolean);
      const title = el.querySelector(".box-style3-header p")?.textContent?.trim() || "";
      blocks.push({ type: "blockquote", text: items.join("\n"), variant: 3, title });
      return;
    }

    if (tag === "div" && el.classList.contains("box-style4")) {
      const text = el.querySelector(".box-style4-body p")?.textContent?.trim() || inlineToText(el).trim();
      const title = el.querySelector(".box-style4-head p")?.textContent?.trim() || "";
      if (text) blocks.push({ type: "blockquote", text, variant: 4, title });
      return;
    }

    if (tag === "div" && el.classList.contains("figure-container")) {
      blocks.push(parseFigureBlock(el));
      return;
    }

    if (tag === "div" && el.classList.contains("table-container")) {
      const table = el.querySelector("table");
      if (table) {
        blocks.push(parseTableBlock(table));
        return;
      }
    }

    if (tag === "table") {
      blocks.push(parseTableBlock(el));
      return;
    }

    if (tag === "figure") {
      blocks.push(parseFigureBlock(el));
      return;
    }

    if (tag.match(/^h[1-6]$/)) {
      const level = Number(tag[1]) || 2;
      const text = inlineToText(el).trim();
      if (text) blocks.push({ type: "heading", level, text });
      return;
    }

    if (tag === "p") {
      const text = inlineToText(el).trim();
      if (text) blocks.push({ type: "paragraph", text });
      return;
    }

    const fallbackText = inlineToText(el).trim();
    if (fallbackText) {
      blocks.push({ type: "paragraph", text: fallbackText });
    }
  });

  return blocks.filter(Boolean);
}

function inlineToText(el) {
  const parts = [];
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent || "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const child = node;
    const tag = child.tagName.toLowerCase();

    if (tag === "br") {
      parts.push("\n");
      return;
    }

    if (tag === "sup") {
      const id = child.getAttribute("data-footnote-id");
      if (id) {
        parts.push(`^${id})`);
      } else {
        parts.push(child.textContent || "");
      }
      return;
    }

    if (tag === "strong" || tag === "b") {
      parts.push(`**${inlineToText(child)}**`);
      return;
    }

    if (tag === "em" || tag === "i") {
      parts.push(`*${inlineToText(child)}*`);
      return;
    }

    parts.push(inlineToText(child));
  });
  return parts.join("");
}

function parseTableBlock(tableEl) {
  const caption = tableEl.querySelector("caption")?.textContent?.trim() || "";
  const rows = [];
  const colWidths = [];
  tableEl.querySelectorAll("colgroup col").forEach((col) => {
    const width = col.getAttribute("style") || col.getAttribute("width") || "";
    const match = width.match(/width\s*:\s*([^;]+)/i);
    const value = match ? match[1].trim() : width.trim();
    if (value) colWidths.push(value.replace("%", "").trim());
  });
  tableEl.querySelectorAll("tr").forEach((tr) => {
    const cells = Array.from(tr.querySelectorAll("th, td")).map((cell) =>
      inlineToText(cell).trim()
    );
    if (cells.length > 0) rows.push(cells);
  });

  const rowsText = rows.map((row) => `| ${row.join(" | ")} |`).join("\n");
  const escapedRowsText = rowsText.replace(/\n/g, "\\n");
  return {
    type: "table",
    caption,
    rowsText: escapedRowsText,
    columnWidths: colWidths.join(","),
  };
}

function parseFigureBlock(el) {
  const img = el.querySelector("img");
  const captionEl =
    el.querySelector("figcaption") || el.querySelector(".figure-caption");
  const style = img?.getAttribute("style") || "";
  const widthMatch = style.match(/width\s*:\s*([0-9.]+)px/i);
  const heightMatch = style.match(/height\s*:\s*([0-9.]+)px/i);
  return {
    type: "figure",
    src: img?.getAttribute("src") || "",
    caption: captionEl?.textContent?.trim() || "",
    width: widthMatch ? Number(widthMatch[1]) : 0,
    height: heightMatch ? Number(heightMatch[1]) : 0,
  };
}

function renderBlockquoteHtml(block) {
  const variant = Number(block.variant) || 1;
  const text = escapeHtml(block.text || "");
  const title = escapeHtml(block.title || "");
  const lines = text
    .split("\n")
    .map((line) => convertInlineFormatting(line))
    .filter(Boolean);

  if (variant === 2) {
    return `
      <div class="box-style2">
        <div class="box-style2-card">
          ${title ? `<strong class="box-style2-card-title">${title}</strong>` : ""}
          <div class="box-style2-card-body">
            <p class="box-style2-card-text">${lines.join("<br>")}</p>
          </div>
        </div>
      </div>
    `.trim();
  }

  if (variant === 3) {
    return `
      <div class="box-style3">
        ${title ? `<div class="box-style3-header"><p>${title}</p></div>` : ""}
        <ul class="box-style3-list">
          ${lines.map((line) => `<li><p>${line}</p></li>`).join("")}
        </ul>
      </div>
    `.trim();
  }

  if (variant === 4) {
    return `
      <div class="box-style4">
        ${title ? `<div class="box-style4-head"><p>${title}</p></div>` : ""}
        <div class="box-style4-body"><p>${lines.join("<br>")}</p></div>
      </div>
    `.trim();
  }

  return `
    <div class="box-style1">
      ${title ? `<div class="box-style1-title"><h3 class="box-style1-title-text">${title}</h3></div>` : ""}
      <div class="box-style1-list">
        <ul class="box-style1-list-items">
          ${lines.map((line) => `<li class="box-style1-list-item"><p>${line}</p></li>`).join("")}
        </ul>
      </div>
    </div>
  `.trim();
}

function renderReferencesHtml(block) {
  const items = (block.itemsText || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (items.length === 0) {
    return '<div class="references-title">참고문헌</div>';
  }
  const list = items
    .map((item) => `<div class="reference-item">${convertInlineFormatting(escapeHtml(item))}</div>`)
    .join("");
  return `<div class="references-title">참고문헌</div>${list}`;
}

function buildColgroup(columnWidths, rows) {
  const widths = parseColumnWidths(columnWidths);
  const colCount = rows.length > 0 ? Math.max(...rows.map((r) => r.length)) : 0;
  if (colCount === 0) return "";

  if (widths.length === 0) return "";
  const normalized = normalizeColumnWidths(widths, colCount);
  const cols = normalized
    .map((w) => `<col style="width: ${w}%">`)
    .join("");
  return `<colgroup>${cols}</colgroup>`;
}

function parseColumnWidths(columnWidths) {
  if (!columnWidths) return [];
  return columnWidths
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((value) => Number(value.replace("%", "")))
    .filter((num) => Number.isFinite(num) && num > 0);
}

function normalizeColumnWidths(widths, colCount) {
  const filled = widths.slice(0, colCount);
  while (filled.length < colCount) filled.push(0);
  const sum = filled.reduce((acc, val) => acc + val, 0);
  if (sum === 0) {
    const equal = 100 / colCount;
    return Array.from({ length: colCount }, () => equal);
  }
  const zeros = filled.filter((val) => val === 0).length;
  const remaining = Math.max(0, 100 - sum);
  const fillValue = zeros > 0 ? remaining / zeros : 0;
  return filled.map((val) => {
    const actual = val === 0 ? fillValue : val;
    return (actual / (sum + remaining)) * 100;
  });
}
